import { useState } from 'react';
import { toast } from 'sonner';
import type { AdminProgrammeOverview, ProgrammeStatus } from '@sparkapp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useChangeProgrammeStatus } from '../../hooks/useProgramme';
import { ChangeProgrammeStageModal } from './ChangeProgrammeStageModal';
import { IN_PROGRESS_STATES, OUTCOME_STATES, STAGE_LABELS, stageChangeEffect } from './programmeLabels';

const STAGES = Object.keys(STAGE_LABELS) as ProgrammeStatus[];

interface Props {
  overview: AdminProgrammeOverview | null | undefined;
  isLoading: boolean;
  isError: boolean;
}

const cardClass = 'bg-pine-card border-pine-line shadow-pine-card rounded-pine-card';

function Tile({
  testId,
  label,
  value,
  subtitle,
  children,
}: {
  testId: string;
  label: string;
  value: string | number;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div data-testid={testId} className="rounded-lg border border-pine-line p-4">
      <p className="text-caption text-pine-ink-soft mb-1">{label}</p>
      <p className="text-heading-3 text-pine-ink font-semibold">{value}</p>
      {subtitle && <p className="text-caption text-pine-ink-soft mt-1">{subtitle}</p>}
      {children}
    </div>
  );
}

/** Dashboard card: CREC programme status, places and onboarding counts. */
export function CrecProgrammeCard({ overview, isLoading, isError }: Props) {
  const [target, setTarget] = useState<ProgrammeStatus | null>(null);
  const change = useChangeProgrammeStatus();

  if (isLoading) {
    return (
      <Card className={cardClass} data-testid="crec-programme-loading">
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-6 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={cardClass}>
        <CardContent className="p-6">
          <p className="text-body text-pine-ink-soft" data-testid="crec-programme-error">
            Failed to load CREC programme.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!overview) {
    return (
      <Card className={cardClass}>
        <CardContent className="p-6">
          <p className="text-body text-pine-ink-soft" data-testid="crec-programme-empty">
            No CREC programme set up.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { programme, places, onboarding } = overview;
  const fill = programme.maxParticipants > 0 ? Math.min(places.registered, programme.maxParticipants) / programme.maxParticipants : 0;

  return (
    <Card className={cardClass} data-testid="crec-programme-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-heading-4 text-pine-ink" data-testid="crec-programme-name">
            {programme.name}
          </CardTitle>
          <p className="text-caption text-pine-ink-soft mt-1">
            {programme.organisation} · {programme.durationWeeks} weeks
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2">
            <Badge data-testid="crec-programme-stage">{STAGE_LABELS[programme.status]}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="programme-stage-menu"
                  className="px-3 py-1 text-xs font-semibold rounded-pine-input border-[1.5px] border-pine-pine text-pine-pine hover:bg-pine-hover-button transition-colors"
                >
                  Change status
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STAGES.filter((s) => s !== programme.status).map((s) => (
                  <DropdownMenuItem key={s} data-testid={`programme-stage-option-${s}`} onSelect={() => setTarget(s)}>
                    {STAGE_LABELS[s]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-caption text-pine-ink-soft mt-1">
            Updated {new Date(overview.generatedAt).toLocaleTimeString()}
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile
            testId="crec-registered"
            label="Registered"
            value={`${places.registered} / ${programme.maxParticipants}`}
            subtitle={places.overflowUsed > 0 ? `+${places.overflowUsed} overflow` : undefined}
          >
            <div className="mt-2 h-2 rounded-full bg-pine-line overflow-hidden">
              <div className="h-full bg-pine-pine" style={{ width: `${Math.round(fill * 100)}%` }} />
            </div>
          </Tile>
          <Tile
            testId="crec-remaining"
            label="Remaining places"
            value={places.remaining}
            subtitle={`hard limit ${programme.hardLimit}`}
          />
          <Tile
            testId="crec-in-onboarding"
            label="In onboarding now"
            value={onboarding.activeHolds}
            subtitle={`${onboarding.expiredHolds} stalled (hold expired)`}
          />
          <Tile
            testId="crec-open-for-invites"
            label="Open for new invites"
            value={places.openForInvites}
            subtitle={programme.status !== 'registration' ? 'registration closed' : undefined}
          />
        </div>

        <div data-testid="crec-onboarding-breakdown">
          <p className="text-caption text-pine-ink-soft mb-2">
            Onboarding by step · {onboarding.inProgress} in progress
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {IN_PROGRESS_STATES.map(({ state, label }) => (
              <div key={state} className="rounded-md border border-pine-line px-3 py-2">
                <p className="text-caption text-pine-ink-soft">{label}</p>
                <p className="font-semibold text-pine-ink" data-testid={`crec-state-${state}`}>
                  {onboarding.byState[state]}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="crec-outcomes">
          <p className="text-caption text-pine-ink-soft mb-2">Outcomes</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {OUTCOME_STATES.map(({ state, label }) => (
              <p key={state} className="text-body text-pine-ink">
                {label}:{' '}
                <span className="font-semibold" data-testid={`crec-outcome-${state}`}>
                  {onboarding.byState[state]}
                </span>
              </p>
            ))}
          </div>
        </div>
      </CardContent>
      {target && (
        <ChangeProgrammeStageModal
          open
          programmeName={programme.name}
          from={programme.status}
          to={target}
          effect={stageChangeEffect(programme.status, target, overview)}
          isLoading={change.isPending}
          onClose={() => setTarget(null)}
          onConfirm={() =>
            change.mutate(
              { slug: programme.slug, status: target, expectedStatus: programme.status },
              {
                onSuccess: () => toast.success(`Programme status changed to ${STAGE_LABELS[target]}`),
                onError: (e) => toast.error(e.message),
                onSettled: () => setTarget(null),
              }
            )
          }
        />
      )}
    </Card>
  );
}
